import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiX,
  FiCopy,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiMinimize2,
  FiBook,
  FiFileText,
  FiZap,
  FiMessageCircle,
  FiStar
} from 'react-icons/fi';
import ChatMarkdown from './ChatMarkdown';
import EnhancedQuizDeck from './EnhancedQuizDeck';
import FlashcardDeck from './FlashcardDeck';

const ContentSidePanel = ({ tabs, activeTabIndex, onTabChange, onClose, onMinimize, isMinimized, onCloseTab }) => {
  const [copied, setCopied] = useState(false);
  // Store quote index per tab ID to prevent state sharing
  const [tabStates, setTabStates] = useState({});
  const [panelWidth, setPanelWidth] = useState(550);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragHandlersRef = useRef({
    move: null,
    end: null
  });
  
  // Get current tab's quote index
  const getCurrentQuoteIndex = (tabId) => tabStates[tabId]?.quoteIndex || 0;
  const setCurrentQuoteIndex = (tabId, index) => {
    setTabStates(prev => ({
      ...prev,
      [tabId]: { ...prev[tabId], quoteIndex: index }
    }));
  };
  
  // Handle resize drag move
  const handleDragMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const newWidth = window.innerWidth - e.clientX;
    const clampedWidth = Math.max(400, Math.min(newWidth, window.innerWidth * 0.6));
    setPanelWidth(clampedWidth);
  }, []);

  // Handle resize drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    document.body.style.userSelect = '';
  }, []);

  // Update refs when handlers change
  useEffect(() => {
    dragHandlersRef.current.move = handleDragMove;
    dragHandlersRef.current.end = handleDragEnd;
  }, [handleDragMove, handleDragEnd]);

  // Handle resize drag start
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    isDraggingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    document.addEventListener('mousemove', dragHandlersRef.current.move);
    document.addEventListener('mouseup', dragHandlersRef.current.end);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isDraggingRef.current) {
        document.removeEventListener('mousemove', dragHandlersRef.current.move);
        document.removeEventListener('mouseup', dragHandlersRef.current.end);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    };
  }, []);

  // Cleanup cursor on drag end
  useEffect(() => {
    if (!isDragging) {
      document.body.style.cursor = '';
    }
  }, [isDragging]);
  
  if (!tabs || tabs.length === 0) return null;
  
  const currentTab = tabs[activeTabIndex];
  
  const handleCopy = () => {
    if (currentTab.type === 'notes' || currentTab.type === 'quotes') {
      const textContent = currentTab.type === 'notes' 
        ? currentTab.content 
        : currentTab.content?.quotes?.map(q => `"${q.quote}" (Page ${q.page_reference})`).join('\n\n');
      
      navigator.clipboard.writeText(textContent || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderQuotesContent = (content) => {
    if (!content || !content.quotes || content.quotes.length === 0) {
      return <div style={{ padding: '20px', color: 'var(--foreground-muted)' }}>No quotes available.</div>;
    }
    
    const currentTabId = currentTab.id || `tab-${activeTabIndex}`;
    const currentQuoteIdx = getCurrentQuoteIndex(currentTabId);
    const currentQuote = content.quotes[currentQuoteIdx];
    
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(to bottom, var(--bg-primary) 0%, #0d0d10 100%)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-secondary)'
        }}>
          <div style={{ fontSize: '13px', color: 'var(--foreground-muted)', fontWeight: '500' }}>
            Quote {currentQuoteIdx + 1} of {content.quotes.length}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setCurrentQuoteIndex(currentTabId, (currentQuoteIdx - 1 + content.quotes.length) % content.quotes.length)}
              style={{
                padding: '6px 12px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--foreground)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FiChevronLeft size={14} />
              Prev
            </button>
            <button
              onClick={() => setCurrentQuoteIndex(currentTabId, (currentQuoteIdx + 1) % content.quotes.length)}
              style={{
                padding: '6px 12px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--foreground)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Next
              <FiChevronRight size={14} />
            </button>
          </div>
        </div>

        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '48px 36px'
        }}>
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.15)',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                opacity: 0.6
              }} />

              {/* Header with category and page */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px', 
                marginBottom: '28px',
                flexWrap: 'wrap',
                marginTop: '6px'
              }}>
                <span style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                  color: '#a1a3ff',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)',
                  letterSpacing: '0.02em'
                }}>
                  {currentQuote.category}
                </span>
                <span style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px', 
                  color: '#fbbf24',
                  fontWeight: '600'
                }}>
                  {(() => {
                    const starCount = (currentQuote.significance?.match(/⭐/g) || []).length;
                    if (starCount > 0) {
                      return Array(starCount).fill(0).map((_, i) => (
                        <FiStar key={i} size={14} fill="#fbbf24" style={{ filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.4))' }} />
                      ));
                    }
                    return currentQuote.significance;
                  })()}
                </span>
                <span style={{ 
                  marginLeft: 'auto', 
                  fontSize: '13px', 
                  color: 'var(--foreground-muted)',
                  fontWeight: '500',
                  letterSpacing: '0.01em'
                }}>
                  Page {currentQuote.page_reference}
                </span>
              </div>

              {/* Quote */}
              <blockquote style={{
                margin: '0 0 36px 0',
                padding: '28px 32px',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                borderLeft: '4px solid #667eea',
                borderRadius: '12px',
                fontSize: '16px',
                lineHeight: '1.9',
                color: 'var(--foreground)',
                fontStyle: 'italic',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                letterSpacing: '0.015em',
                fontWeight: '400'
              }}>
                "{currentQuote.quote}"
              </blockquote>

              {/* Analysis */}
              {currentQuote.analysis && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    marginBottom: '20px', 
                    color: 'var(--foreground)',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    opacity: 0.9
                  }}>
                    Analysis
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{
                      padding: '20px',
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(102, 126, 234, 0.04) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      boxShadow: '0 2px 12px rgba(102, 126, 234, 0.08)'
                    }}>
                      <div style={{ fontSize: '12px', color: '#667eea', marginBottom: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        <FiZap size={14} /> Key Idea
                      </div>
                      <div style={{ fontSize: '15px', lineHeight: '1.8', color: 'var(--foreground)', letterSpacing: '0.01em' }}>
                        {currentQuote.analysis.key_idea}
                      </div>
                    </div>

                    <div style={{
                      padding: '20px',
                      background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.08) 0%, rgba(52, 211, 153, 0.04) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(52, 211, 153, 0.2)',
                      boxShadow: '0 2px 12px rgba(52, 211, 153, 0.08)'
                    }}>
                      <div style={{ fontSize: '12px', color: '#34d399', marginBottom: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        <FiZap size={14} /> Why Important
                      </div>
                      <div style={{ fontSize: '15px', lineHeight: '1.8', color: 'var(--foreground)', letterSpacing: '0.01em' }}>
                        {currentQuote.analysis.why_important}
                      </div>
                    </div>

                    {currentQuote.analysis.practical_use && (
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(251, 191, 36, 0.04) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(251, 191, 36, 0.2)',
                        boxShadow: '0 2px 12px rgba(251, 191, 36, 0.08)'
                      }}>
                        <div style={{ fontSize: '12px', color: '#fbbf24', marginBottom: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                          <FiFileText size={14} /> Practical Use
                        </div>
                        <div style={{ fontSize: '15px', lineHeight: '1.8', color: 'var(--foreground)', letterSpacing: '0.01em' }}>
                          {currentQuote.analysis.practical_use}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Discussion Prompts */}
              {currentQuote.discussion_prompts && currentQuote.discussion_prompts.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: 'var(--foreground)', display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '0.03em', textTransform: 'uppercase', opacity: 0.9 }}>
                    <FiMessageCircle size={16} /> Discussion Questions
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', lineHeight: '1.8', color: 'var(--foreground)', letterSpacing: '0.01em' }}>
                    {currentQuote.discussion_prompts.map((prompt, idx) => (
                      <li key={idx} style={{ marginBottom: '12px', paddingLeft: '4px' }}>{prompt}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Keywords */}
              {currentQuote.keywords && currentQuote.keywords.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--foreground-muted)', marginBottom: '12px', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                    Keywords
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {currentQuote.keywords.map((keyword, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '6px 12px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          border: '1px solid var(--border)',
                          color: 'var(--foreground-muted)',
                          letterSpacing: '0.01em'
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentTab.type) {
      case 'notes':
        return (
          <div style={{ 
            height: '100%', 
            overflowY: 'auto',
            background: 'linear-gradient(to bottom, var(--bg-primary) 0%, #0d0d10 100%)'
          }}>
            <div style={{
              maxWidth: '850px',
              margin: '0 auto',
              padding: '48px 36px',
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.15)',
                borderRadius: '16px',
                padding: '40px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Decorative gradient overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  opacity: 0.6
                }} />
                
                {/* Content */}
                <div 
                  className="study-notes-content"
                  style={{
                    fontSize: '16px',
                    lineHeight: '1.9',
                    color: 'var(--foreground)',
                    letterSpacing: '0.015em'
                  }}
                >
                  <style>{`
                    .study-notes-content h1 { margin: 32px 0 20px 0; font-size: 28px; line-height: 1.3; letter-spacing: 0.02em; }
                    .study-notes-content h2 { margin: 28px 0 18px 0; font-size: 24px; line-height: 1.4; letter-spacing: 0.015em; }
                    .study-notes-content h3 { margin: 24px 0 16px 0; font-size: 20px; line-height: 1.5; letter-spacing: 0.01em; }
                    .study-notes-content h4 { margin: 20px 0 14px 0; font-size: 18px; line-height: 1.6; }
                    .study-notes-content p { margin: 16px 0; }
                    .study-notes-content ul, .study-notes-content ol { margin: 16px 0; padding-left: 28px; }
                    .study-notes-content li { margin: 10px 0; padding-left: 6px; }
                    .study-notes-content blockquote { margin: 20px 0; padding: 16px 24px; background: rgba(102, 126, 234, 0.06); border-left: 4px solid #667eea; border-radius: 8px; }
                    .study-notes-content code { background: rgba(102, 126, 234, 0.1); padding: 2px 6px; border-radius: 4px; font-size: 14px; }
                    .study-notes-content pre { margin: 20px 0; padding: 20px; background: rgba(0, 0, 0, 0.3); border-radius: 8px; overflow-x: auto; }
                    .study-notes-content strong { font-weight: 600; color: var(--foreground); }
                    .study-notes-content em { font-style: italic; color: var(--foreground); }
                    .study-notes-content hr { margin: 28px 0; border: none; border-top: 1px solid var(--border); }
                  `}</style>
                  <ChatMarkdown content={currentTab.content} />
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'quotes':
        return renderQuotesContent(currentTab.content);
      
      case 'quiz':
        return (
          <div style={{ height: '100%' }}>
            <EnhancedQuizDeck
              quiz={currentTab.content}
              onComplete={() => {}}
              onExit={onClose}
            />
          </div>
        );
      
      case 'flashcards':
        return (
          <div style={{ height: '100%' }}>
            <FlashcardDeck
              flashcards={currentTab.content}
              onComplete={() => {}}
              onExit={onClose}
            />
          </div>
        );
      
      default:
        return <div style={{ padding: '20px' }}>Unsupported content type</div>;
    }
  };

  if (isMinimized) {
    return (
      <motion.div
        initial={{ width: 550, opacity: 1 }}
        animate={{ width: 60, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ 
          type: 'spring',
          stiffness: 300,
          damping: 30,
          mass: 0.8
        }}
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '12px',
          gap: '8px',
          zIndex: 100,
          boxShadow: '-2px 0 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <button
          onClick={onMinimize}
          style={{
            width: '40px',
            height: '40px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--foreground)'
          }}
        >
          <FiMaximize2 size={16} />
        </button>
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => {
              onTabChange(index);
              onMinimize();
            }}
            style={{
              width: '40px',
              height: '40px',
              background: index === activeTabIndex ? 'var(--accent-primary)' : 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: index === activeTabIndex ? 'white' : 'var(--foreground)',
              fontSize: '18px'
            }}
          >
            {tab.icon}
          </button>
        ))}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ width: 60, opacity: 0 }}
      animate={{ width: 550, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ 
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 0.8
      }}
      ref={panelRef}
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: `${panelWidth}px`,
        maxWidth: '60vw',
        background: 'var(--bg-primary)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 100,
        boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)'
      }}
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleDragStart}
        style={{
          position: 'absolute',
          left: -2,
          top: 0,
          bottom: 0,
          width: '8px',
          cursor: 'ew-resize',
          background: isDragging ? 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' : 'transparent',
          transition: isDragging ? 'none' : 'all 0.2s ease',
          zIndex: 1000,
          boxShadow: isDragging ? '0 0 20px rgba(102, 126, 234, 0.5)' : 'none'
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.target.style.background = 'rgba(102, 126, 234, 0.4)';
            e.target.style.boxShadow = '0 0 10px rgba(102, 126, 234, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.target.style.background = 'transparent';
            e.target.style.boxShadow = 'none';
          }
        }}
      />
      
      {/* Header with tabs */}
      <div style={{
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-primary)'
      }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: '4px', overflowX: 'auto' }}>
          {tabs.map((tab, index) => (
            <div
              key={tab.id || index}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                background: index === activeTabIndex ? 'var(--bg-secondary)' : 'transparent',
                border: index === activeTabIndex ? '1px solid var(--border)' : '1px solid transparent',
                borderRadius: '6px', padding: '4px 4px 4px 8px', transition: 'all 0.15s'
              }}
            >
              <button
                onClick={() => onTabChange(index)}
                style={{
                  padding: '4px 8px', background: 'transparent', border: 'none', cursor: 'pointer',
                  fontWeight: '500',
                  color: index === activeTabIndex ? 'var(--foreground)' : 'var(--foreground-muted)',
                  display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'
                }}
              >
                {tab.type === 'quotes' && <FiBook size={14} />}
                {tab.type === 'notes' && <FiFileText size={14} />}
                {tab.type === 'quiz' && <FiZap size={14} />}
                {tab.type === 'flashcards' && <FiZap size={14} />}
                {tab.type !== 'quotes' && tab.type !== 'notes' && tab.type !== 'quiz' && tab.type !== 'flashcards' && <FiFileText size={14} />}
                <span>{tab.title}</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onCloseTab?.(index); }}
                style={{
                  padding: '2px', background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--foreground-muted)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', borderRadius: '4px', opacity: 0.6, transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(255,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.background = 'transparent'; }}
                title="Close tab"
              >
                <FiX size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Actions bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: 'var(--bg-secondary)'
        }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--foreground)' }}>
            {currentTab.title}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(currentTab.type === 'notes' || currentTab.type === 'quotes') && (
              <button
                onClick={handleCopy}
                style={{
                  padding: '6px 10px',
                  background: copied ? 'rgba(52, 211, 153, 0.15)' : 'var(--bg-tertiary)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: copied ? '#34d399' : 'var(--foreground)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            <button
              onClick={onMinimize}
              style={{
                width: '30px',
                height: '30px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--foreground)'
              }}
            >
              <FiMinimize2 size={14} />
            </button>
            <button
              onClick={onClose}
              style={{
                width: '30px',
                height: '30px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--foreground)'
              }}
            >
              <FiX size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {renderContent()}
      </div>

      {/* Footer with document info */}
      {currentTab.documentName && (
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          color: 'var(--foreground-muted)'
        }}>
          <FiFileText size={14} />
          <span>From: <strong style={{ color: 'var(--foreground)' }}>{currentTab.documentName}</strong></span>
        </div>
      )}
    </motion.div>
  );
};

export default ContentSidePanel;
